import React, {useRef, useState} from 'react';

import {isAfter, isBefore, isValid} from 'date-fns';

import {Label} from '../Label';

import {CustomDatePicker, RangeInputContainer, RangeSeparator} from './styled';

type DatePair = [Date | null, Date | null];

interface Props {
    value?: DatePair;
    onChange?: (value: DatePair | undefined) => void;
    hasError?: boolean;
    label?: string;
    formFieldName?: string;
    keepHeaderSelectOpenOnBlur?: boolean;
}

export const DateRange = ({
    value,
    onChange,
    label,
    hasError,
    keepHeaderSelectOpenOnBlur,
}: Props) => {
    const [valueFrom, valueTo] = value || [null, null];
    const [openedPicker, setOpenedPicker] = useState<0 | 1 | null>(null);
    const activePickerRef = useRef<0 | 1 | null>(null);
    const onValueChange = (date: Date | null, index: 0 | 1) => {
        const start2020Year = new Date('2020-01-01');
        const isOlder2020Year = date && isValid(date) && isBefore(date, start2020Year);
        if (isOlder2020Year) date = start2020Year;

        const isAfterCurrentDate = date && isValid(date) && isAfter(date, new Date());
        if (isAfterCurrentDate) date = new Date();

        if (!onChange) return;

        const result: DatePair = index === 0 ? [date, valueTo] : [valueFrom, date];
        onChange(result.every((elem) => elem == null) ? undefined : result);
    };

    const getPickerTrackingProps = (index: 0 | 1) =>
        keepHeaderSelectOpenOnBlur
            ? {
                  onCalendarOpen: () => {
                      if (activePickerRef.current === null) {
                          activePickerRef.current = index;
                          setOpenedPicker(index);
                      }
                  },
                  onCalendarClose: () => {
                      if (activePickerRef.current === index) activePickerRef.current = null;
                      setOpenedPicker((current) => (current === index ? null : current));
                  },
                  ...(openedPicker !== null && openedPicker !== index && {open: false}),
              }
            : {};

    const selectActivePicker = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!keepHeaderSelectOpenOnBlur) return;

        const target = event.target as HTMLElement;
        const inputContainer = target.closest('.react-datepicker__input-container');
        if (!inputContainer) return;

        const inputContainers = Array.from(
            event.currentTarget.querySelectorAll('.react-datepicker__input-container'),
        );
        const index = inputContainers.indexOf(inputContainer);
        if (index !== 0 && index !== 1) return;

        activePickerRef.current = index;
        setOpenedPicker(index);
    };

    return (
        <Label text={label} hasError={hasError}>
            <RangeInputContainer
                {...(keepHeaderSelectOpenOnBlur && {onMouseDownCapture: selectActivePicker})}
            >
                <CustomDatePicker
                    selected={valueFrom}
                    onChange={(date: any) => onValueChange(date, 0)}
                    selectsStart
                    startDate={valueFrom}
                    endDate={valueTo}
                    maxDate={valueTo}
                    hasError={hasError}
                    keepHeaderSelectOpenOnBlur={keepHeaderSelectOpenOnBlur}
                    {...getPickerTrackingProps(0)}
                />
                <RangeSeparator />
                <CustomDatePicker
                    selected={valueTo}
                    onChange={(date: any) => onValueChange(date, 1)}
                    selectsEnd
                    startDate={valueFrom}
                    endDate={valueTo}
                    minDate={valueFrom}
                    hasError={hasError}
                    keepHeaderSelectOpenOnBlur={keepHeaderSelectOpenOnBlur}
                    {...getPickerTrackingProps(1)}
                />
            </RangeInputContainer>
        </Label>
    );
};
